import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { CheckSquareOutlined, CloseCircleOutlined, MessageOutlined, PushpinOutlined, ShopOutlined } from '@ant-design/icons';

import { StatCard } from '../ds';
import { friendlyError } from '../../lib/api';
import { OPENING_TASKS_KEYS, openingTasksApi, type MyOpeningTasksDto, type MyRoomDto } from '../../lib/api/openingTasks';
import { OpeningHero } from './OpeningHero';
import { isNotForViewer, retryOpeningQuery } from './opening-query';
import { RoomsPanel } from './RoomsPanel';
import { NotificationsPanel, TrajectoryPanel } from './SidePanels';
import { roomsNotice, stageProgress } from './stage-tasks';
import { StageTasksPanel } from './StageTasksPanel';
import { isRoomOnReview } from './utils';

export interface OpeningTasksState {
  query: UseQueryResult<MyOpeningTasksDto>;
  /** Партнёр на открытии: есть карточка открытия или хотя бы одно помещение. */
  active: boolean;
  /** Ждём ответа — страница не перестраивается рывком под партнёра. */
  pending: boolean;
}

/** Данные блока «Открытие» для «Моих задач» (ключ ['wiki','opening','my-tasks']). */
export function useOpeningTasks(): OpeningTasksState {
  const query = useQuery({
    queryKey: OPENING_TASKS_KEYS.myTasks,
    queryFn: () => openingTasksApi.myTasks(),
    // Один повтор только на сбой сервера/сети; 401/403/404 — сразу «не показываем».
    retry: retryOpeningQuery,
    staleTime: 30_000,
  });
  const d = query.data;
  const active = !!d && !!d.partner && (!!d.pipeline || d.rooms.length > 0);
  return { query, active, pending: query.isLoading };
}

/**
 * Плашка над задачами этапа: по помещениям есть новые сообщения куратора или моменты к исправлению. Сами помещения
 * и переписка живут в визарде «Открытие точки» → «Обращения». Ноль — плашки нет.
 */
function RoomsNotice({ rooms }: { rooms: MyRoomDto[] }): JSX.Element | null {
  const notice = roomsNotice(rooms);
  if (!notice) return null;
  return (
    <div className="fk-ot-callout" data-tone="warn" role="status">
      <MessageOutlined />
      <div style={{ minWidth: 0 }}>
        {notice.text}.{' '}
        <Link to="/opening?tab=appeals" className="fk-ot-more-link">
          Открыть обращения
        </Link>
      </div>
    </div>
  );
}

/**
 * Блок «Открытие» партнёра. С карточкой открытия (владелец 31.10.2026): только текущий этап — тёмная карточка с
 * кнопкой «Весь план открытия», плашка по помещениям, задачи этапа «в работе» и заявка на переход; всё остальное
 * (диаграмма, этапы, чек-листы, помещения, обращения, траектория, результаты УК) — в визарде «Открытие точки».
 * Без карточки, но с помещениями — как раньше: плитки, помещения с перепиской, траектория, уведомления.
 * Не партнёр / нет открытия — ничего не рисуем.
 */
export function OpeningTasksSection({ state }: { state: OpeningTasksState }): JSX.Element | null {
  const { query, active } = state;
  if (query.isLoading) return null;
  if (query.error) {
    if (isNotForViewer(query.error)) return null;
    return (
      <div className="fk-ot" style={{ marginBottom: 20 }}>
        <div className="fk-ot-callout" data-tone="error">
          <CloseCircleOutlined />
          <div style={{ minWidth: 0 }}>
            Не удалось загрузить задачи открытия: {friendlyError(query.error)}{' '}
            <button type="button" className="fk-ot-linkbtn" onClick={() => void query.refetch()}>
              Повторить
            </button>
          </div>
        </div>
      </div>
    );
  }
  const d = query.data;
  if (!d || !active) return null;

  if (d.pipeline) {
    return (
      <div className="fk-ot">
        <OpeningHero data={d} />
        <RoomsNotice rooms={d.rooms} />
        <StageTasksPanel data={d} />
      </div>
    );
  }

  // Задачи карточки (план открытия) и прежние пункты этапа — одним счётчиком.
  const tasks = stageProgress(d);
  const openMoments = d.rooms.reduce((s, r) => s + r.openMoments, 0);
  const onReview = d.rooms.filter((r) => isRoomOnReview(r.stageCode, r.status)).length;
  const unread = d.rooms.reduce((s, r) => s + r.unread, 0);

  return (
    <div className="fk-ot">
      <div className="fk-ot-stats">
        <StatCard
          icon={<CheckSquareOutlined />}
          tone="orange"
          label="Задачи этапа"
          value={tasks.done}
          suffix={`/ ${tasks.total}`}
          hint="выполнено"
        />
        <StatCard icon={<PushpinOutlined />} tone="red" label="Моменты к исправлению" value={openMoments} hint="по всем помещениям" />
        <StatCard icon={<ShopOutlined />} tone="blue" label="Помещения на проверке" value={onReview} hint={`из ${d.rooms.length} отправленных`} />
        <StatCard icon={<MessageOutlined />} tone="green" label="Новые сообщения" value={unread} hint="от куратора" />
      </div>

      <OpeningHero data={d} />

      <div className="fk-ot-grid">
        <div className="fk-ot-stack">
          <RoomsPanel rooms={d.rooms} />
        </div>
        <div className="fk-ot-stack">
          <TrajectoryPanel groups={d.trajectory} />
          <NotificationsPanel items={d.notifications} />
        </div>
      </div>
    </div>
  );
}
